import { Language } from '@yleisradio/areena-types';
import { DateTime } from 'luxon';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIMEZONE = 'Europe/Helsinki';

export function formatEpgDate(date: string, language: Language): string {
  const dateObject = new Date(date);

  return dateObject.toLocaleDateString(`${language}-FI`, {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function isoDateFromJSDate(date: Date): string {
  return date.toISOString().substring(0, 10);
}

type DateBoundaries = { first: string; last: string };

// Uses Luxon, use only on server
export function getEpgDateBoundaries(now = new Date()): DateBoundaries {
  const firstDate = DateTime.fromJSDate(now, { zone: TIMEZONE })
    .minus({ days: 7 })
    .toISODate();

  const lastDate = DateTime.fromJSDate(now, { zone: TIMEZONE })
    .plus({ weeks: 5, days: -1 })
    .toISODate();

  if (!firstDate || !lastDate) {
    throw new Error('Invalid date');
  }

  return {
    first: firstDate,
    last: lastDate,
  };
}

type SurroundingDates = { previous: string; next: string };

export function getSurroundingEpgDates(currentDate: string): SurroundingDates {
  const reference = new Date(currentDate);

  const previous = new Date(reference);
  previous.setUTCDate(reference.getUTCDate() - 1);

  const next = new Date(reference);
  next.setUTCDate(reference.getUTCDate() + 1);

  return {
    previous: isoDateFromJSDate(previous),
    next: isoDateFromJSDate(next),
  };
}

// Uses Luxon, use only on server
export function isValidEpgDate(something: string, now = new Date()): boolean {
  if (!DATE_PATTERN.test(something)) return false;

  const dt = DateTime.fromISO(something, { zone: TIMEZONE });

  if (!dt.isValid) return false;

  const min = DateTime.fromISO(dateToEpgDate(now), { zone: TIMEZONE })
    .minus({ days: 7 })
    .startOf('day');

  const max = DateTime.fromISO(dateToEpgDate(now), { zone: TIMEZONE })
    .plus({ weeks: 5, days: -1 })
    .startOf('day');

  return dt >= min && dt <= max;
}

// Uses Luxon, use only on server
export function dateToEpgDate(date: Date): string {
  const dt = DateTime.fromJSDate(date, { zone: TIMEZONE }).minus({ hours: 4 });
  const isoDt = dt.toISODate();

  if (!dt.isValid || isoDt === null) throw new Error('Invalid Date');

  return isoDt;
}

// Uses Luxon, use only on server
export function getTodaysEpgDate(now = new Date()): string {
  return dateToEpgDate(now);
}

// Uses Luxon, use only on server
export function isEpgDateInFuture(date: string, now = new Date()): boolean {
  return new Date(date) > new Date(dateToEpgDate(now));
}
